<!--
  REV-21368 — single document of the closing (Pure View).

  Selector contract: tests bind to `data-testid`, `aria-pressed` and `data-*` markers, never to
  styling classes and never to translated prose as an anchor. See e2e-lab/document-detail.lab.spec.ts.

  Two places where ADR-0009 (accepted 11.08.2026) overrules the live prototype:
    · NO `Löschen` in the header (§4) — the prototype still has it, in red, with a lock tooltip
    · `Projekt-Akte` is enabled and selectable (§9a) — an earlier AC draft said "disabled"

  The picker is ONE step, unlike the hub's: the `Gültigkeit` panel stands in this sidebar in all
  three states, so asking for validity inside the picker too would put the same question twice on
  one screen. Choosing an account or the Projekt-Akte IS the whole gesture.
-->
<div class="dd" data-testid="document-detail">
    <!-- Precedence loading > error > document (pattern: documents-hub). -->
    <div class="dd-state" *ngIf="loading">
        <div class="dd-loading" data-testid="document-detail-loading">
            <div class="dd-skeleton" aria-hidden="true"></div>
            <div class="dd-skeleton" aria-hidden="true"></div>
        </div>
    </div>

    <div class="dd-state" *ngIf="!loading && error">
        <div class="dd-error" data-testid="document-detail-error">
            {{ 'abcp_document_detail_error' | i18next }}
        </div>
    </div>

    <ng-container *ngIf="!loading && !error && document">
        <!-- KOPF — genau zwei Aktionen. Kein `Löschen`: es waere die einzige irreversible
             Aktion des ganzen Flusses, auf revisionsrelevanten Daten (§4). -->
        <header class="dd-head" data-testid="document-detail-header">
            <div class="dd-head__identity" data-testid="document-detail-name">
                <lucide-icon class="dd-head__icon" name="file-text" [size]="22" aria-hidden="true"></lucide-icon>
                <h2
                    class="dd-head__title"
                    [hamTooltip]="document.filename"
                    tooltipClass="abcp"
                    [tooltipOnlyWhenTruncated]="true"
                    >{{ document.filename }}</h2
                >
            </div>

            <div class="dd-head__actions">
                <!-- Only on a relevant document. A not relevant one is brought back by the
                     button in its empty state; the header does not offer the same button twice. -->
                <ham-button
                    data-testid="document-detail-not-relevant"
                    *ngIf="!document.notRelevant"
                    variant="white"
                    lucideIcon="eye-off"
                    data-state="mark"
                    [hamTooltip]="'abcp_documents_not_relevant_mark' | i18next"
                    tooltipClass="abcp"
                    (buttonClick)="onNotRelevantToggle()"
                >
                    {{ 'abcp_document_detail_not_relevant_mark' | i18next }}
                </ham-button>

                <ham-button
                    data-testid="document-detail-close"
                    *ngIf="showBack"
                    variant="white"
                    icon="assets/hamIcons/x.svg"
                    (buttonClick)="onClose()"
                >
                    {{ 'abcp_document_detail_close' | i18next }}
                </ham-button>
            </div>
        </header>

        <!-- REV-21964 - U2: WO DIE DATEI STEHT, direkt unter dem Kopf.

             Hier und nicht in der Seitenleiste: es ist eine Aussage ueber DAS DOKUMENT, nicht ueber
             seine Zuordnung, und die Seitenleiste erzaehlt ausschliesslich von der Zuordnung.

             WARNUNG: Der Block NIMMT NICHTS WEG. Konto, Abstimmung und Vorschau bleiben unberuehrt,
             wenn die Datei in SharePoint geloescht wurde (C1) - ADR-0009 §4 kennt keinen Loeschweg.
             Der einzige Rueckzug bleibt `Nicht relevant` im Kopf, und den waehlt der Treuhaender.
             Deshalb steht hier auch kein eigener Knopf, sondern ein Satz, der auf den vorhandenen
             zeigt: zwei Stellen fuer dieselbe Handlung waeren zwei Stellen, die auseinanderlaufen. -->
        <div
            class="dd-upstream"
            data-testid="document-detail-upstream"
            *ngIf="hasUpstreamNotice"
            [attr.data-state]="document.upstreamState"
            [attr.data-urgent]="isUpstreamNoticeUrgent"
            [class.dd-upstream--urgent]="isUpstreamNoticeUrgent"
            [attr.role]="isUpstreamNoticeUrgent ? 'alert' : 'status'"
        >
            <lucide-icon
                class="dd-upstream__icon"
                [name]="document.upstreamState === 'replaced' ? 'history' : 'unlink'"
                [size]="16"
                aria-hidden="true"
            ></lucide-icon>
            <div class="dd-upstream__text">
                <strong class="dd-upstream__state" data-testid="document-detail-upstream-state">{{
                    upstreamStateLabelKey | i18next
                }}</strong>
                <span data-testid="document-detail-upstream-notice">{{ upstreamNoticeKey | i18next }}</span>
                <!-- Wann es sich GEAENDERT hat, nicht wann zuletzt geschaut wurde - das erste ist
                     eine Tatsache ueber den Ordner, das zweite eine ueber uns. -->
                <span
                    class="dd-upstream__meta"
                    data-testid="document-detail-upstream-changed-at"
                    *ngIf="document.upstreamStateChangedAt"
                    >{{
                        'abcp_upstream_changed_at'
                            | i18next: { date: (document.upstreamStateChangedAt | date: 'dd.MM.yyyy') }
                    }}</span
                >
                <!-- Der Hinweis auf `Nicht relevant` im Kopf. Eine Empfehlung, keine Handlung. -->
                <span class="dd-upstream__meta">{{ 'abcp_upstream_action_not_relevant' | i18next }}</span>
            </div>
        </div>

        <!-- ID2 - dieselben BYTES unter einem zweiten Namen in SharePoint. Bewusst KEINE zweite
             Zeile und kein zweites Dokument: es ist eine Tatsache ueber den Ordner des Treuhaenders,
             und ein zweites Dokument waere eine Abstimmung, die es doppelt gibt. -->
        <div class="dd-alias" data-testid="document-detail-alias" *ngIf="document.aliasHint">
            <lucide-icon class="dd-alias__icon" name="copy" [size]="14" aria-hidden="true"></lucide-icon>
            <span>{{ 'abcp_upstream_alias_names' | i18next: { names: document.aliasHint } }}</span>
        </div>

        <!-- REV-21369 — die Breiten der beiden Spalten stellt der Nutzer ein (Prototyp): ein Trenner
             zwischen Beleg und Seitenleiste, ziehbar, per Pfeiltaste bedienbar, Doppelklick = Hälfte. -->
        <div
            #body
            class="dd-body"
            [class.dd-body--resizing]="splitDrag !== null"
            [style.grid-template-columns]="gridColumns"
        >
            <!-- VORSCHAU — der Browser-eigene Viewer (§13). Kein pdf.js, kein eigener Renderer;
                 die Komponente holt die Datei nie, sie reicht nur die URL weiter.

                 REV-21363: die GETEILTE Komponente, nicht mehr inline. Sie traegt die
                 Schema-Allowlist, die `bypassSecurityTrustResourceUrl` absichert (SRR REV-21368
                 F1) — der Konto-Dokumentbereich braucht dieselbe Vorschau, und die Kontrolle darf
                 es nur EINMAL geben. -->
            <abcp-document-preview
                *ngIf="!viewerSlot"
                class="dd-preview"
                [previewKind]="document.previewKind"
                [previewUrl]="document.previewUrl"
                [title]="document.filename"
            ></abcp-document-preview>

            <!-- REV-21369 — der Beleg-Viewer (ADR-0021 E6), PROJIZIERT vom Wirt. Diese Ansicht
                 zeichnet ihn nicht und holt keine Bytes; sie macht nur Platz. Ohne Projektion bleibt
                 die Vorschau oben, was sie war. -->
            <div class="dd-viewer" data-testid="document-detail-viewer" *ngIf="viewerSlot">
                <ng-content select="[abcpDocumentViewer]"></ng-content>
            </div>

            <div
                class="dd-splitter"
                data-testid="document-detail-splitter"
                role="separator"
                tabindex="0"
                aria-orientation="vertical"
                [attr.aria-valuenow]="splitPercent"
                [attr.aria-valuemin]="SPLIT_MIN"
                [attr.aria-valuemax]="100 - SPLIT_MIN"
                [attr.aria-label]="'abcp_document_detail_splitter' | i18next"
                (mousedown)="onSplitStart($event)"
                (dblclick)="resetSplit()"
                (keydown)="onSplitKey($event)"
            ></div>

            <!-- SEITENLEISTE — der Zustand ist ABGELEITET, nicht gespeichert (§9). -->
            <aside class="dd-panel" data-testid="document-detail-sidebar" [attr.data-state]="sidebarState">
                <!-- F5 (#84) — der Schreibfehler EINES Vorgangs (Bestaetigen/Ablehnen/Ruecknahme).
                     AUSSERHALB der drei Zustands-Bloecke: er muss unabhaengig davon stehen, ob
                     gerade ein Vorschlag oder eine bestehende Zuordnung angezeigt wird (Faelle 15
                     und 16 im Integrations-Contract). `document-detail-error` bleibt der
                     LADEFEHLER und ersetzt die ganze Seite — dieser tut das NICHT. -->
                <div
                    class="dd-proposal-error"
                    data-testid="document-detail-proposal-error"
                    *ngIf="proposalFailed || writeFailed"
                    role="alert"
                >
                    <lucide-icon name="alert-circle" [size]="14" aria-hidden="true"></lucide-icon>
                    {{ 'abcp_document_detail_write_error' | i18next }}
                </div>

                <!-- NOT RELEVANT: nothing to file, so the filing parts give way to this.
                     The comments below stay (why it is not relevant is worth a comment). -->
                <div class="dd-not-relevant" data-testid="document-detail-not-relevant-state" *ngIf="document.notRelevant">
                    <lucide-icon class="dd-not-relevant__glyph" name="eye-off" [size]="20" aria-hidden="true"></lucide-icon>
                    <div class="dd-not-relevant__title">{{ 'abcp_document_detail_not_relevant_title' | i18next }}</div>
                    <div class="dd-not-relevant__body">{{ 'abcp_document_detail_not_relevant_body' | i18next }}</div>
                    <ham-button
                        data-testid="document-detail-not-relevant-restore"
                        variant="white"
                        size="sm"
                        [class.dd-busy]="isBusy('relevance')"
                        [class.dd-busy-spin]="spins('relevance')"
                        [attr.aria-busy]="isBusy('relevance') || null"
                        [icon]="spins('relevance') ? null : 'assets/hamIcons/eye-outline.svg'"
                        [lucideIcon]="spins('relevance') ? 'loader-circle' : null"
                        (buttonClick)="onNotRelevantToggle()"
                    >
                        {{ 'abcp_document_detail_not_relevant_restore' | i18next }}
                    </ham-button>
                </div>

                <ng-container *ngIf="!document.notRelevant">

                    <!-- 1 · AGENT-VORSCHLAG. Ein Vorschlag ist KEINE Zuordnung (§9). -->
                    <div
                        class="dd-agent"
                        data-testid="document-detail-proposal"
                        *ngIf="sidebarState === 'proposal'"
                        [class.dd-busy]="isBusy('proposal')"
                        [attr.aria-busy]="isBusy('proposal') || null"
                    >
                        <div class="dd-agent__head">
                            <lucide-icon name="sparkles" [size]="14" aria-hidden="true"></lucide-icon>
                            <span>{{ 'abcp_document_detail_proposal_title' | i18next }}</span>
                            <span class="dd-agent__confidence" data-testid="document-detail-proposal-confidence">{{
                                confidenceLabel
                            }}</span>
                            <!-- F8 — die Zahl beschreibt den BELEGTYP, nicht die Kontowahl; dieselbe
                                 Beschriftung wie die Hub-Spalte, geteilt ueber `abcp_documents_col_confidence`. -->
                            <span
                                class="dd-agent__confidence-label"
                                data-testid="document-detail-proposal-confidence-label"
                                >{{ 'abcp_documents_col_confidence' | i18next }}</span
                            >
                        </div>

                        <div class="dd-agent__account" data-testid="document-detail-proposal-account">
                            {{ document.account?.label }}
                        </div>

                        <div class="dd-agent__actions">
                            <ham-button
                                data-testid="document-detail-proposal-accept"
                                variant="primary"
                                size="sm"
                                [class.dd-busy-spin]="spins('proposal', 'accept')"
                                [icon]="spins('proposal', 'accept') ? null : 'assets/hamIcons/check.svg'"
                                [lucideIcon]="spins('proposal', 'accept') ? 'loader-circle' : null"
                                (buttonClick)="onProposalAccept()"
                            >
                                {{ 'abcp_document_detail_proposal_accept' | i18next }}
                            </ham-button>
                            <ham-button
                                data-testid="document-detail-proposal-reject"
                                variant="white"
                                size="sm"
                                [class.dd-busy-spin]="spins('proposal', 'reject')"
                                [icon]="spins('proposal', 'reject') ? null : 'assets/hamIcons/x.svg'"
                                [lucideIcon]="spins('proposal', 'reject') ? 'loader-circle' : null"
                                (buttonClick)="onProposalReject()"
                            >
                                {{ 'abcp_documents_proposal_reject' | i18next }}
                            </ham-button>
                        </div>

                        <div
                            class="dd-agent__pending"
                            data-testid="document-detail-proposal-pending"
                            *ngIf="proposalPending"
                            aria-live="polite"
                        >
                            <lucide-icon name="loader-circle" [size]="13" aria-hidden="true"></lucide-icon>
                            {{ 'abcp_documents_proposal_pending' | i18next }}
                        </div>
                    </div>

                    <!-- 2 · ZUGEORDNET. Projekt-Akte ist abgelegt und relevant, nur ohne Konto (§9a). -->
                    <div
                        class="dd-assigned"
                        data-testid="document-detail-assigned"
                        *ngIf="sidebarState === 'assigned'"
                        [class.dd-busy]="isBusy('assignment')"
                        [attr.aria-busy]="isBusy('assignment') || null"
                    >
                        <div class="dd-assigned__label">{{ 'abcp_document_detail_assigned_title' | i18next }}</div>

                        <!-- N1 (#84) — IN der Zuordnungs-Karte: WER zugeordnet hat. Genau bei
                             `assignedBy === 'agent'` — Altbestand (`assignedBy` fehlt) behauptet nichts
                             ueber eine Herkunft, die niemand kennt. -->
                        <div
                            class="dd-agent-notice"
                            data-testid="document-detail-agent-notice"
                            *ngIf="document.assignedBy === 'agent'"
                        >
                            <lucide-icon name="bot-message-square" [size]="14" aria-hidden="true"></lucide-icon>
                            <div class="dd-agent-notice__text">{{ 'abcp_documents_agent_notice_title' | i18next }}</div>
                        </div>

                        <div class="dd-assigned__row">
                            <div class="dd-assigned__target">
                                <span
                                    class="dd-assigned__project-file"
                                    data-testid="document-detail-assigned-project-file"
                                    *ngIf="isProjectFile"
                                >
                                    <lucide-icon
                                        class="dd-project-file-glyph"
                                        name="clipboard-list"
                                        [size]="14"
                                        aria-hidden="true"
                                    ></lucide-icon>
                                    {{ 'abcp_document_detail_project_file' | i18next }}
                                </span>
                                <span
                                    class="dd-assigned__account"
                                    data-testid="document-detail-assigned-account"
                                    *ngIf="!isProjectFile"
                                    [hamTooltip]="document.account?.label"
                                    tooltipClass="abcp"
                                    [tooltipOnlyWhenTruncated]="true"
                                    >{{ document.account?.label }}</span
                                >
                            </div>

                            <div class="dd-assigned__actions">
                                <!-- N2 (#84) — die ZUORDNUNG zurueckgeben, keine ERSETZUNG: eigene
                                     Ausgabe (`assignmentRemove`), eigener Knopf. „Zuordnung entfernen",
                                     nicht „loeschen" — es gibt kein Loeschen in diesem Fluss (§4). Ein
                                     anderes Konto waehlt man danach im Waehler, der dann erscheint; ein
                                     eigenes „Aendern" tat dasselbe in einem Schritt weniger klar. -->
                                <ham-button
                                    data-testid="document-detail-assigned-remove"
                                    variant="white"
                                    size="sm"
                                    [class.dd-busy-spin]="spins('assignment')"
                                    [lucideIcon]="spins('assignment') ? 'loader-circle' : 'undo-2'"
                                    (buttonClick)="onAssignmentRemove()"
                                >
                                    {{ 'abcp_document_detail_assigned_remove' | i18next }}
                                </ham-button>
                            </div>
                        </div>
                    </div>

                    <!-- REV-22010 — WARUM die KI kein Konto vorgeschlagen hat, aus einer geschlossenen
                         Liste. Nur ueber dem Waehler eines UNABGELEGTEN Belegs — dort ist die Frage offen. -->
                    <div
                        class="dd-agent-notice dd-proposal-refusal"
                        data-testid="document-detail-proposal-refusal"
                        *ngIf="proposalRefusalKey"
                        [attr.data-reason]="document.proposalRefusal"
                    >
                        <lucide-icon name="sparkles" [size]="14" aria-hidden="true"></lucide-icon>
                        <div class="dd-agent-notice__text">
                            <strong>{{ proposalRefusalTitleKey | i18next }}</strong>
                            {{ proposalRefusalKey | i18next }}
                        </div>
                    </div>

                    <!-- 3 · KONTO ZUORDNEN. EIN Schritt: die Gueltigkeit fragt das stehende Panel
                         darunter, nicht der Waehler. -->
                    <div
                        class="dd-picker"
                        data-testid="document-detail-picker"
                        *ngIf="sidebarState === 'picker'"
                        [class.dd-busy]="isBusy('assignment')"
                        [attr.aria-busy]="isBusy('assignment') || null"
                    >
                        <div class="dd-picker__head">
                            <b class="dd-picker__title">{{ 'abcp_document_detail_picker_title' | i18next }}</b>
                            <span class="dd-picker__counter" data-testid="document-detail-picker-counter"
                                >{{
                                    'abcp_document_detail_picker_counter'
                                        | i18next: { matched: matchCount, total: totalCount }
                                }}</span
                            >
                        </div>

                        <!-- Angeheftet UEBER dem Suchfeld und bleibt beim Suchen stehen: die
                             Projekt-Akte ist ein Ablageziel, kein Suchergebnis (§9a). Im Hub liegt
                             sie IN der Liste — nicht verwechseln. -->
                        <button
                            class="dd-picker__project-file"
                            type="button"
                            data-testid="document-detail-picker-project-file"
                            [class.is-chosen]="isChosen('projectFile')"
                            [attr.data-chosen]="isChosen('projectFile') || null"
                            (click)="onProjectFilePick()"
                        >
                            <lucide-icon
                                class="dd-project-file-glyph"
                                name="clipboard-list"
                                [size]="14"
                                aria-hidden="true"
                            ></lucide-icon>
                            <span class="dd-picker__project-file-text">
                                <span class="dd-picker__row-title">{{
                                    'abcp_document_detail_project_file' | i18next
                                }}</span>
                                <span class="dd-picker__row-sub">{{
                                    'abcp_document_detail_project_file_hint' | i18next
                                }}</span>
                            </span>
                        </button>

                        <div class="dd-picker__search">
                            <lucide-icon name="search" [size]="14" aria-hidden="true"></lucide-icon>
                            <input
                                type="text"
                                data-testid="document-detail-picker-search"
                                [value]="pickerSearch"
                                [attr.placeholder]="'abcp_document_detail_picker_search_placeholder' | i18next"
                                [attr.aria-label]="'abcp_document_detail_picker_search_placeholder' | i18next"
                                (input)="onPickerSearch($any($event.target).value)"
                            />
                        </div>

                        <div class="dd-picker__list">
                            <button
                                class="dd-picker__row"
                                type="button"
                                data-testid="document-detail-picker-account"
                                *ngFor="let account of filteredAccounts; trackBy: trackById"
                                [attr.data-account-id]="account.id"
                                [class.is-chosen]="isChosen(account.id)"
                                [attr.data-chosen]="isChosen(account.id) || null"
                                (click)="onAccountPick(account)"
                            >
                                <span class="dd-picker__number">{{ account.number }}</span>
                                <span class="dd-picker__name">{{ account.name }}</span>
                                <span
                                    class="dd-picker__position"
                                    [hamTooltip]="account.klasse"
                                    tooltipClass="abcp"
                                    [tooltipOnlyWhenTruncated]="true"
                                    >{{ account.klasse }}</span
                                >
                            </button>

                            <div
                                class="dd-picker__empty"
                                data-testid="document-detail-picker-empty"
                                *ngIf="!filteredAccounts.length"
                            >
                                {{ 'abcp_document_detail_picker_empty' | i18next }}
                            </div>
                        </div>
                    </div>

                    <!-- REV-21869 #164 (AP8c) — BELEGWERT. Eigenes Panel, eigener `data-state` (P7): die
                         Deutung ist aus assignment/filing nicht ableitbar. Kein Panel im VORSCHLAG (W2).
                         Eine Zeile je gelesenem Wert — ein Saldo, beim Depotauszug Marktwert und
                         Einstandswert. Nie ein roher Code oder Satz aus dem Backend. -->
                    <div
                        class="dd-interpretation"
                        data-testid="document-detail-interpretation"
                        *ngIf="showInterpretation"
                        [attr.data-state]="interpretationDataState"
                    >
                        <div class="dd-interpretation__label">
                            {{ 'abcp_document_detail_interpretation_title' | i18next }}
                        </div>

                        <ng-container *ngIf="interpretation?.state === 'value'; else interpretationEmptyState">
                            <div
                                class="dd-interpretation__value"
                                data-testid="document-detail-interpretation-value"
                                *ngFor="let value of interpretation?.values"
                                [attr.data-label]="value.label"
                                [attr.data-amount-minor]="value.amountMinor"
                                [attr.data-currency]="value.currency"
                                [attr.data-date]="value.date || null"
                            >
                                <div class="dd-interpretation__amount" data-testid="document-detail-interpretation-line">
                                    <span class="dd-interpretation__value-label">{{ valueLabelKey(value.label) | i18next }}</span>
                                    {{ value.amount | finNumber: '1.2-2' }} {{ value.currency }}
                                    <span class="dd-interpretation__date" *ngIf="value.date">{{
                                        'abcp_document_detail_value_as_of' | i18next: { date: dateLabel(value.date) }
                                    }}</span>
                                </div>
                                <div
                                    class="dd-interpretation__quote"
                                    data-testid="document-detail-interpretation-quote"
                                    *ngIf="value.quote"
                                >
                                    {{ 'abcp_document_quote' | i18next: { quote: quoteText(value.quote) } }}
                                </div>
                            </div>
                        </ng-container>
                        <div
                            class="dd-interpretation__document-date"
                            data-testid="document-detail-document-date"
                            *ngIf="interpretation?.documentDate"
                        >
                            {{ 'abcp_document_detail_document_date' | i18next: { date: dateLabel(interpretation?.documentDate) } }}
                        </div>
                        <ng-template #interpretationEmptyState>
                            <div class="dd-interpretation__state">
                                {{ interpretationStateLabelKey | i18next }}
                            </div>
                            <!-- REV-22010 — WHY, from a closed list (never a raw code, amount or free text).
                                 Only when the backend knows a reason; otherwise the headline stands alone. -->
                            <div
                                class="dd-interpretation__reason"
                                data-testid="document-detail-interpretation-reason"
                                *ngIf="interpretationReasonKey"
                                [attr.data-reason]="interpretation?.reason"
                            >
                                {{ interpretationReasonKey | i18next }}
                            </div>
                        </ng-template>
                    </div>

                    <!-- REV-22010 — hat die KI den Beleg GELESEN, und wie gut? Eigener Block, eigener
                         `data-state`: der Lesezustand der Datei ist weder die Zuordnung noch die Deutung.
                         Nur Urteile — nie eine Seite, eine Zelle oder ein Betrag. -->
                    <div
                        class="dd-reading"
                        data-testid="document-detail-reading"
                        *ngIf="hasReadingBlock"
                        [attr.data-state]="readingDataState"
                        [class.dd-reading--warn]="document.readingState === 'failed' || document.readingState === 'deferredDailyCap'"
                    >
                        <div class="dd-reading__label">{{ readingKeys.title | i18next }}</div>
                        <div class="dd-reading__state" data-testid="document-detail-reading-state">
                            {{ (readingStateLabelKey || readingKeys.read) | i18next }}
                        </div>
                        <div
                            class="dd-reading__quality"
                            data-testid="document-detail-reading-quality"
                            *ngFor="let line of readingQualityKeys"
                            [attr.data-key]="line.key"
                        >
                            {{ line.key | i18next: line.params }}
                        </div>
                    </div>

                    <!-- GUELTIGKEIT — der STEHENDE Schalter, in allen drei Zustaenden. Er aendert die
                         Gueltigkeit NACHTRAEGLICH, ohne neu abzulegen (§5). -->
                    <div
                        class="dd-validity"
                        data-testid="document-detail-validity"
                        [class.dd-busy]="isBusy('validity')"
                        [attr.aria-busy]="isBusy('validity') || null"
                    >
                        <div class="dd-validity__label">{{ 'abcp_document_detail_validity_title' | i18next }}</div>
                        <div class="dd-validity__options">
                            <button
                                class="dd-validity__option"
                                type="button"
                                data-testid="document-detail-validity-closing"
                                [attr.aria-pressed]="shownValidity === 'closing'"
                                (click)="onValidityPick('closing')"
                            >
                                {{ 'abcp_document_detail_validity_closing' | i18next }}
                            </button>
                            <button
                                class="dd-validity__option"
                                type="button"
                                data-testid="document-detail-validity-long-term"
                                [attr.aria-pressed]="shownValidity === 'longTerm'"
                                (click)="onValidityPick('longTerm')"
                            >
                                <lucide-icon name="clock" [size]="12" aria-hidden="true"></lucide-icon>
                                {{ 'abcp_document_detail_validity_long_term' | i18next }}
                            </button>
                        </div>
                    </div>

                </ng-container>

                <!-- REV-21369 — die Kommentare zum Beleg (ADR-0021 E6), PROJIZIERT vom Wirt. -->
                <div class="dd-notes" data-testid="document-detail-notes" *ngIf="notesSlot">
                    <ng-content select="[abcpDocumentNotes]"></ng-content>
                </div>
            </aside>
        </div>
    </ng-container>
</div>
